
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Reagent } from "@/services/reagentService";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TestTube, Loader2 } from "lucide-react";

interface UserReagentSelectProps {
  userReagents: Reagent[];
  selectedReagentId: string | null;
  setSelectedReagentId: (id: string) => void;
  isLoading: boolean;
}

const UserReagentSelect: React.FC<UserReagentSelectProps> = ({
  userReagents,
  selectedReagentId,
  setSelectedReagentId,
  isLoading
}) => {
  // Handle the selection change
  const handleReagentChange = (value: string) => {
    setSelectedReagentId(value);
  };

  // Get details of the selected reagent
  const selectedReagent = selectedReagentId 
    ? userReagents.find(r => r.id === selectedReagentId) 
    : null;

  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <p className="text-sm text-muted-foreground mb-4">
          Offer one of your reagents in exchange. The owner will receive this reagent if they accept your request.
        </p>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mr-2" />
            <span className="text-muted-foreground">Loading your reagents...</span>
          </div>
        ) : userReagents.length === 0 ? (
          <div className="bg-muted p-4 rounded-md">
            <p className="text-sm text-muted-foreground">
              You don't have any reagents available to offer. Upload reagents first or choose to offer credits instead.
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <label htmlFor="reagent-select" className="text-sm font-medium leading-none">
                Select a reagent to offer
              </label>
              <Select 
                value={selectedReagentId || ""} 
                onValueChange={handleReagentChange}
              >
                <SelectTrigger id="reagent-select" className="w-full">
                  <SelectValue placeholder="Select a reagent to offer" />
                </SelectTrigger>
                <SelectContent>
                  {userReagents.map((reagent) => (
                    <SelectItem key={reagent.id} value={reagent.id}>
                      {reagent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedReagent && (
              <div className="bg-secondary/30 p-3 rounded-md mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <TestTube className="h-4 w-4 text-primary" />
                  <span className="font-medium">Selected Reagent Details</span>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{selectedReagent.name}</p>
                  </div>
                  
                  {selectedReagent.amount && selectedReagent.amount_unit && (
                    <div>
                      <p className="text-sm text-muted-foreground">Amount</p>
                      <p>{selectedReagent.amount} {selectedReagent.amount_unit}</p>
                    </div>
                  )}
                  
                  {selectedReagent.catalogue_number && (
                    <div>
                      <p className="text-sm text-muted-foreground">Catalog Number</p>
                      <p>{selectedReagent.catalogue_number}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default UserReagentSelect;
